import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ShieldCheck, Users, Crown, TrendingUp, Workflow, Megaphone, Sparkles, Plus, ArrowLeft, Loader2, Activity, Coins, PoundSterling, Search, X, AlertTriangle, CircleCheck, Pause, LogOut, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { Wordmark } from "@/components/Brand";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import ProspectsTab from "@/components/admin/ProspectsTab";
import AdvertisersTab from "@/components/admin/AdvertisersTab";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "customers", label: "Customers" },
  { id: "workflows", label: "Workflows" },
  { id: "activity", label: "Activity" },
  { id: "revenue", label: "Revenue" },
  { id: "mogul", label: "Mogul Clients" },
  { id: "prospects", label: "Prospects" },
  { id: "advertisers", label: "Advertisers" },
  { id: "partner", label: "Partner Page Builder" },
  { id: "adsync", label: "Ad Sync" },
];

const AdminCommandCenter = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [customerFilter, setCustomerFilter] = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050B14] text-white flex items-center justify-center">
        <Loader2 className="text-cyan-400 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#050B14] text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md" data-testid="admin-forbidden">
          <ShieldCheck size={36} className="mx-auto text-red-400" />
          <h1 className="mt-4 font-heading text-3xl font-black">Restricted.</h1>
          <p className="mt-2 text-slate-400">
            The Command Center is admin-only. Your email isn't on the admin list yet.
          </p>
          <Link
            to="/dashboard"
            className="mt-6 inline-block px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm"
            data-testid="admin-back-to-dashboard"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const jumpTo = (nextTab, filter = null) => {
    setTab(nextTab);
    if (filter) setCustomerFilter(filter);
    else setCustomerFilter(null);
  };

  return (
    <div className="min-h-screen bg-[#050B14] text-white" data-testid="admin-command-center">
      <SEO title="Command Center" description="ClydeFlow admin command center." path="/admin" noindex />

      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050B14]/80 border-b border-cyan-400/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="text-slate-300 hover:text-white" data-testid="admin-back-link">
              <ArrowLeft size={18} />
            </Link>
            <Link to="/"><Wordmark className="h-12" /></Link>
            <span className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.2em] rounded-sm">
              <ShieldCheck size={12} /> Command Center
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-xs text-slate-400 font-mono uppercase tracking-widest" data-testid="admin-user">
              {user.email}
            </span>
            <Link
              to="/dashboard"
              data-testid="admin-to-dashboard"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0A192F] border border-white/10 hover:border-cyan-400/40 text-slate-300 hover:text-white font-mono text-[10px] uppercase tracking-widest rounded-sm"
            >
              <LayoutDashboard size={12} /> Dashboard
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0A192F] border border-white/10 hover:border-red-400/40 text-slate-300 hover:text-red-300 font-mono text-[10px] uppercase tracking-widest rounded-sm"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={[
                "px-4 py-3 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px whitespace-nowrap transition-colors",
                tab === t.id ? "border-cyan-400 text-white" : "border-transparent text-slate-400 hover:text-white",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {tab === "overview" && <OverviewTab jumpTo={jumpTo} />}
        {tab === "customers" && (
          <CustomersTab filter={customerFilter} onClearFilter={() => setCustomerFilter(null)} />
        )}
        {tab === "workflows" && <WorkflowsTab />}
        {tab === "activity" && <ActivityTab />}
        {tab === "revenue" && <RevenueTab />}
        {tab === "mogul" && <MogulTab />}
        {tab === "prospects" && <ProspectsTab />}
        {tab === "advertisers" && <AdvertisersTab />}
        {tab === "partner" && <PartnerPageTab />}
        {tab === "adsync" && <AdSyncTab />}
      </main>
    </div>
  );
};

// ---------------- Overview ----------------
const OverviewTab = ({ jumpTo }) => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/system-health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  // Each card has an onClick that jumps to a relevant tab/filter
  const cards = stats
    ? [
        {
          id: "users",
          label: "Total Users",
          v: stats.total_users,
          icon: Users,
          onClick: () => jumpTo("customers"),
        },
        {
          id: "paid",
          label: "Paid Users",
          v: stats.paid_users,
          icon: TrendingUp,
          onClick: () => jumpTo("customers", { plan: "paid" }),
        },
        {
          id: "mogul",
          label: "Mogul Clients",
          v: stats.mogul_clients,
          icon: Crown,
          onClick: () => jumpTo("mogul"),
        },
        {
          id: "mrr",
          label: "MRR (£)",
          v: stats.mrr_gbp,
          icon: PoundSterling,
          onClick: () => jumpTo("revenue"),
        },
        {
          id: "revenue",
          label: "Revenue (£)",
          v: stats.revenue_gbp,
          icon: PoundSterling,
          onClick: () => jumpTo("revenue"),
        },
        {
          id: "workflows",
          label: "Workflows Built",
          v: stats.workflows_built,
          icon: Workflow,
          onClick: () => jumpTo("workflows"),
        },
        {
          id: "wingman",
          label: "Tam Msgs",
          v: stats.wingman_messages,
          icon: Sparkles,
          onClick: () => jumpTo("activity"),
        },
      ]
    : [];

  return (
    <div data-testid="admin-overview">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400 mb-3">
        Command Center · Overview
      </p>
      <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter">
        The Empire, at a glance.
      </h1>
      <p className="mt-2 text-slate-400 text-sm">
        Tap any card to drill into it.
      </p>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.id}
              onClick={c.onClick}
              type="button"
              className="text-left p-6 bg-[#0A192F] border border-white/10 hover:border-cyan-400/40 hover:bg-[#0A192F]/80 rounded-sm transition-colors group focus:outline-none focus:border-cyan-400"
              data-testid={`admin-stat-${c.id}`}
            >
              <Icon size={18} className="text-cyan-300" />
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 group-hover:text-slate-400">
                {c.label}
              </p>
              <p className="mt-2 font-heading text-3xl font-black text-white">{c.v}</p>
              <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-cyan-400/60 group-hover:text-cyan-300">
                Drill in →
              </p>
            </button>
          );
        })}
        {!stats && (
          <p className="col-span-full text-slate-500 text-sm">Loading…</p>
        )}
      </div>

      {/* System health */}
      {health && (
        <div className="mt-10" data-testid="admin-system-health">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">
            System health
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <HealthChip ok={health.meta_configured} label="Meta API" />
            <HealthChip ok={health.google_configured} label="Google Drive" />
            <HealthChip ok={health.stripe_configured} label="Stripe" />
            <HealthRow label="Last workflow run" v={health.last_workflow_run?.started_at} sub={health.last_workflow_run?.status} />
            <HealthRow label="Last Meta event" v={health.last_meta_event?.received_at} sub={health.last_meta_event?.object} />
            <HealthRow label="Last payment" v={health.last_payment?.created_at} sub={`${health.last_payment?.payment_status || ""} ${health.last_payment?.amount || ""}`} />
          </div>
        </div>
      )}
    </div>
  );
};

const HealthChip = ({ ok, label }) => (
  <div className={`px-3 py-2 bg-[#0A192F] border rounded-sm flex items-center gap-2 ${ok ? "border-emerald-400/30" : "border-amber-400/30"}`}>
    {ok ? <CircleCheck size={14} className="text-emerald-300" /> : <AlertTriangle size={14} className="text-amber-300" />}
    <span className="text-slate-200 text-xs uppercase font-mono tracking-widest">{label}</span>
    <span className={`ml-auto text-[10px] font-mono uppercase tracking-widest ${ok ? "text-emerald-300" : "text-amber-300"}`}>
      {ok ? "OK" : "missing"}
    </span>
  </div>
);

const HealthRow = ({ label, v, sub }) => (
  <div className="px-3 py-2 bg-[#0A192F] border border-white/10 rounded-sm">
    <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">{label}</p>
    <p className="mt-1 text-slate-200 text-xs truncate">{v || "—"}</p>
    {sub && <p className="text-[10px] text-cyan-300/80 font-mono uppercase tracking-widest">{sub}</p>}
  </div>
);

// ---------------- Customers ----------------
const CustomersTab = ({ filter, onClearFilter }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showComp, setShowComp] = useState(false);
  const [draft, setDraft] = useState({ email: "", name: "", plan: "pro", wingman_tokens: 1000, note: "" });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/customers");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = items;
    if (filter?.plan === "paid") {
      rows = rows.filter((u) => u.plan && u.plan !== "free");
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.user_id || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [items, filter, search]);

  const createComp = async () => {
    if (!draft.email.trim()) return toast.error("Email required.");
    setCreating(true);
    try {
      await api.post("/admin/comp-account", draft);
      toast.success("Comp account ready.");
      setShowComp(false);
      setDraft({ email: "", name: "", plan: "pro", wingman_tokens: 1000, note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="admin-customers">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-heading text-2xl font-bold">Customers</h2>
          {filter?.plan === "paid" && (
            <button
              onClick={onClearFilter}
              data-testid="customers-clear-filter"
              className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-cyan-400/20"
            >
              Paid only <X size={10} />
            </button>
          )}
          <span className="text-xs text-slate-400 font-mono uppercase tracking-widest">
            {filtered.length} / {items.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email / name"
              data-testid="customers-search"
              className="pl-7 pr-3 py-1.5 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-xs rounded-sm w-48"
            />
          </div>
          <button
            onClick={() => setShowComp(true)}
            data-testid="admin-comp-open"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF]"
          >
            <Plus size={14} /> Comp Account
          </button>
        </div>
      </div>

      <div className="mt-6 bg-[#0A192F] border border-white/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/10">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Tokens</th>
              <th className="px-4 py-3">Trial Ends</th>
              <th className="px-4 py-3">Channels</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr
                key={u.user_id}
                data-testid={`admin-customer-${u.user_id}`}
                className={`border-b border-white/5 hover:bg-white/5 ${u.suspended ? "opacity-60" : ""}`}
              >
                <td className="px-4 py-3">
                  <p className="text-white">{u.email}</p>
                  <p className="text-xs text-slate-500">{u.name}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">
                    {u.plan || "free"}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-300 font-mono">{u.wingman_tokens ?? 0}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{u.trial_end_date?.slice(0, 10) || "—"}</td>
                <td className="px-4 py-3 text-slate-300 text-xs">
                  {(u.connected_channels || []).join(", ") || "—"}
                </td>
                <td className="px-4 py-3">
                  {u.suspended ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-400/10 text-red-300 border border-red-400/30 font-mono text-[9px] uppercase tracking-widest">
                      <Pause size={9} /> Suspended
                    </span>
                  ) : (
                    <span className="text-slate-500 text-xs">
                      {u.is_guest ? "Comp" : u.role === "admin" ? "Admin" : "Active"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-500 font-mono text-[10px]">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setEditing(u)}
                    data-testid={`customer-edit-${u.user_id}`}
                    className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 hover:text-cyan-200"
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500">No customers match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showComp && (
        <CompModal
          draft={draft}
          setDraft={setDraft}
          creating={creating}
          onClose={() => setShowComp(false)}
          onCreate={createComp}
        />
      )}

      {editing && (
        <ManageUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const CompModal = ({ draft, setDraft, creating, onClose, onCreate }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0A192F] border border-cyan-400/30 cf-glow rounded-sm p-6" data-testid="admin-comp-modal">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">Create comp/guest account</p>
      <h4 className="font-heading text-xl font-bold">New free account</h4>
      <div className="mt-4 space-y-3">
        <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="email@business.com" data-testid="admin-comp-email" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name (optional)" data-testid="admin-comp-name" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
        <select value={draft.plan} onChange={(e) => setDraft({ ...draft, plan: e.target.value })} data-testid="admin-comp-plan" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm">
          {["free", "starter", "pro", "empire", "mogul"].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input type="number" value={draft.wingman_tokens} onChange={(e) => setDraft({ ...draft, wingman_tokens: e.target.value })} placeholder="Wingman tokens" data-testid="admin-comp-tokens" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
        <textarea value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Internal note (optional)" rows={2} data-testid="admin-comp-note" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancel</button>
        <button onClick={onCreate} disabled={creating} data-testid="admin-comp-create" className="px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-[#33F3FF] disabled:opacity-60">
          {creating ? "Creating…" : "Create"}
        </button>
      </div>
    </div>
  </div>
);

const ManageUserModal = ({ user, onClose, onSaved }) => {
  const [plan, setPlan] = useState(user.plan || "free");
  const [tokensAdd, setTokensAdd] = useState(0);
  const [trialDays, setTrialDays] = useState(0);
  const [suspended, setSuspended] = useState(!!user.suspended);
  const [note, setNote] = useState(user.admin_note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { plan, suspended, note };
      if (Number(tokensAdd)) payload.wingman_tokens_add = Number(tokensAdd);
      if (Number(trialDays)) payload.trial_extend_days = Number(trialDays);
      await api.patch(`/admin/customers/${user.user_id}`, payload);
      toast.success("User updated.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${user.email}? This wipes their workflows, runs, and Tam history.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/customers/${user.user_id}`);
      toast.success("User deleted.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0A192F] border border-cyan-400/30 cf-glow rounded-sm p-6" data-testid="admin-manage-user-modal">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">Manage user</p>
        <h4 className="font-heading text-xl font-bold">{user.email}</h4>
        <p className="text-xs text-slate-400">{user.user_id} · Joined {user.created_at?.slice(0, 10)}</p>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Plan</span>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="manage-plan" className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm">
              {["free", "starter", "pro", "empire", "mogul"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Top-up Tam tokens (+/-)</span>
            <input type="number" value={tokensAdd} onChange={(e) => setTokensAdd(e.target.value)} data-testid="manage-tokens" className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Extend trial (days)</span>
            <input type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} data-testid="manage-trial" className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={suspended} onChange={(e) => setSuspended(e.target.checked)} data-testid="manage-suspend" />
            <span className="text-sm text-slate-200">Suspend account</span>
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Admin note</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="manage-note" className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          </label>
        </div>

        <div className="mt-5 flex justify-between gap-2">
          <button onClick={remove} disabled={busy || user.role === "admin"} data-testid="manage-delete" className="px-3 py-2 text-xs text-red-300 hover:text-red-200 font-mono uppercase tracking-widest disabled:opacity-40">
            Delete user
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="manage-save" className="px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-[#33F3FF] disabled:opacity-60">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------- Workflows (platform-wide) ----------------
const WorkflowsTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/workflows").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-workflows">
      <h2 className="font-heading text-2xl font-bold">Workflows · platform-wide</h2>
      <p className="text-slate-400 text-sm mt-1">Every Empire Builder workflow across the platform. Read-only.</p>
      <div className="mt-6 bg-[#0A192F] border border-white/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/10">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Owner</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Runs</th>
              <th className="px-4 py-3">Last Run</th>
              <th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.workflow_id} data-testid={`admin-workflow-${w.workflow_id}`} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white">{w.spec?.name || "(untitled)"}</td>
                <td className="px-4 py-3 text-slate-300 text-xs">{w.owner_email || w.user_id}</td>
                <td className="px-4 py-3">
                  <span className={[
                    "px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border",
                    w.status === "paused" ? "bg-amber-400/10 text-amber-300 border-amber-400/30" :
                    w.status === "errored" ? "bg-red-400/10 text-red-300 border-red-400/30" :
                    "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
                  ].join(" ")}>
                    {w.status || "draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-300 font-mono">{w.runs ?? 0}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{w.last_run_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-[10px]">{w.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No workflows yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------- Activity (counts only) ----------------
const ActivityTab = () => {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/admin/activity").then((r) => setA(r.data)).catch(() => {});
  }, []);

  const tiles = a
    ? [
        { label: "Signups · 24h",   v: a.signups.h24,   sub: `${a.signups.d7} / 7d · ${a.signups.d30} / 30d`, icon: Users },
        { label: "Tam msgs · 24h", v: a.wingman_messages.h24, sub: `${a.wingman_messages.d7} / 7d · ${a.wingman_messages.d30} / 30d`, icon: Sparkles },
        { label: "Workflows built · 24h", v: a.workflows_built.h24, sub: `${a.workflows_built.d7} / 7d`, icon: Workflow },
        { label: "Workflow runs · 24h", v: a.workflow_runs.h24, sub: `${a.workflow_runs.d7} / 7d · failed: ${a.workflow_runs.failed_h24}`, icon: Activity },
        { label: "Meta DMs · 24h", v: a.meta_dms_received.h24, sub: `${a.meta_dms_received.d7} / 7d`, icon: Megaphone },
      ]
    : [];
  return (
    <div data-testid="admin-activity">
      <h2 className="font-heading text-2xl font-bold">Platform activity</h2>
      <p className="text-slate-400 text-sm mt-1">Counts only — message content is never exposed here.</p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="p-6 bg-[#0A192F] border border-white/10 rounded-sm">
              <Icon size={18} className="text-cyan-300" />
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{t.label}</p>
              <p className="mt-2 font-heading text-3xl font-black text-white">{t.v}</p>
              <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">{t.sub}</p>
            </div>
          );
        })}
        {!a && <p className="text-slate-500 text-sm col-span-full">Loading…</p>}
      </div>
    </div>
  );
};

// ---------------- Revenue ----------------
const RevenueTab = () => {
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/admin/revenue").then((res) => setR(res.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-revenue">
      <h2 className="font-heading text-2xl font-bold">Revenue</h2>
      <p className="text-slate-400 text-sm mt-1">Per-plan breakdown + lifetime revenue from Stripe paid transactions.</p>
      {!r && <p className="mt-6 text-slate-500 text-sm">Loading…</p>}
      {r && (
        <>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-6 bg-[#0A192F] border border-cyan-400/30 cf-glow rounded-sm">
              <PoundSterling size={18} className="text-cyan-300" />
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">MRR (£)</p>
              <p className="mt-2 font-heading text-3xl font-black text-white">{r.mrr_gbp}</p>
            </div>
            <div className="p-6 bg-[#0A192F] border border-white/10 rounded-sm">
              <Coins size={18} className="text-cyan-300" />
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Lifetime (£)</p>
              <p className="mt-2 font-heading text-3xl font-black text-white">{r.lifetime_revenue_gbp}</p>
            </div>
            <div className="p-6 bg-[#0A192F] border border-white/10 rounded-sm">
              <Coins size={18} className="text-cyan-300" />
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Lifetime ($)</p>
              <p className="mt-2 font-heading text-3xl font-black text-white">{r.lifetime_revenue_usd}</p>
              <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">{r.paid_transactions} paid tx</p>
            </div>
          </div>
          <div className="mt-8 bg-[#0A192F] border border-white/10 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/10">
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Customers</th>
                  <th className="px-4 py-3">Price (£/mo)</th>
                  <th className="px-4 py-3">MRR (£)</th>
                </tr>
              </thead>
              <tbody>
                {r.by_plan.map((b) => (
                  <tr key={b.plan} data-testid={`revenue-plan-${b.plan}`} className="border-b border-white/5">
                    <td className="px-4 py-3 text-white font-mono uppercase tracking-widest text-xs">{b.plan}</td>
                    <td className="px-4 py-3 text-slate-300">{b.customers}</td>
                    <td className="px-4 py-3 text-slate-300">{b.monthly_price_gbp}</td>
                    <td className="px-4 py-3 text-cyan-300 font-bold">{b.monthly_revenue_gbp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------- Mogul / Partner / AdSync (unchanged) ----------------
const MogulTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/mogul-clients").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-mogul">
      <h2 className="font-heading text-2xl font-bold">Mogul Clients · £299 deal</h2>
      <p className="text-slate-400 text-sm mt-1">Your done-for-you customers. Run their onboarding from here.</p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((u) => (
          <div key={u.user_id} className="p-4 bg-[#0A192F] border border-cyan-400/30 cf-glow rounded-sm" data-testid={`admin-mogul-${u.user_id}`}>
            <div className="flex items-center gap-3">
              {u.picture ? <img src={u.picture} className="h-10 w-10 rounded-full border border-white/10" alt="" /> : <div className="h-10 w-10 bg-cyan-400/10 rounded-full" />}
              <div>
                <p className="font-heading font-bold text-white">{u.name}</p>
                <p className="text-xs text-slate-400">{u.email}</p>
              </div>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-cyan-300">Joined · {u.created_at?.slice(0, 10)}</p>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-500 text-sm">No Mogul clients yet.</p>}
      </div>
    </div>
  );
};

const PartnerPageTab = () => {
  const [form, setForm] = useState({ business_name: "", niche: "", location: "Paisley", tone: "mogul" });
  const [busy, setBusy] = useState(false);
  const [pages, setPages] = useState([]);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/partner-pages");
    setPages(data || []);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.business_name.trim() || !form.niche.trim()) return toast.error("Business name + niche required.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partner-page/generate", form);
      setPreview(data);
      toast.success("Partner page generated.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-partner">
      <h2 className="font-heading text-2xl font-bold">Partner Page Builder</h2>
      <p className="text-slate-400 text-sm mt-1">AI-generates landing copy + image prompt for any Mogul partner.</p>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0A192F] border border-white/10 rounded-sm p-5 space-y-3">
          <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Business name" data-testid="admin-partner-business" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          <input value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} placeholder="Niche (e.g. local café, gym, salon)" data-testid="admin-partner-niche" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" data-testid="admin-partner-location" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          <button onClick={generate} disabled={busy} data-testid="admin-partner-generate" className="w-full py-3 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-[#33F3FF] disabled:opacity-60">
            {busy ? "Generating…" : "Generate Page"}
          </button>
        </div>

        <div className="bg-[#0A192F] border border-white/10 rounded-sm p-5" data-testid="admin-partner-preview">
          {preview ? (
            <div className="space-y-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">{preview.business_name}</p>
              <p className="font-heading text-2xl font-black text-white">{preview.spec?.headline}</p>
              <p className="text-slate-300 text-sm">{preview.spec?.subheadline}</p>
              <ul className="space-y-1">
                {(preview.spec?.value_props || []).map((v, i) => <li key={i} className="text-slate-300 text-sm">• {v}</li>)}
              </ul>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">CTA · {preview.spec?.cta_primary} / {preview.spec?.cta_secondary}</p>
              <p className="text-xs text-slate-400">Hero image prompt: {preview.spec?.hero_image_prompt}</p>
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Generated page will preview here.</p>
          )}
        </div>
      </div>

      <div className="mt-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">Library</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {pages.map((p) => (
            <button key={p.page_id} onClick={() => setPreview(p)} data-testid={`admin-partner-page-${p.page_id}`} className="text-left bg-[#0A192F] border border-white/10 hover:border-cyan-400/40 rounded-sm p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-cyan-300">{p.business_name}</p>
              <p className="mt-1 text-white font-heading font-bold truncate">{p.spec?.headline}</p>
              <p className="text-slate-500 text-[10px] mt-1">{p.created_at?.slice(0, 10)}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdSyncTab = () => {
  const [form, setForm] = useState({ headline: "", body: "", cta_url: "", target_segment: "all_mogul" });
  const [busy, setBusy] = useState(false);
  const [syncs, setSyncs] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/ad-syncs");
    setSyncs(data || []);
  };
  useEffect(() => { load(); }, []);

  const broadcast = async () => {
    if (!form.headline.trim() || !form.cta_url.trim()) return toast.error("Headline + CTA URL required.");
    const segLabel = { all_mogul: "all Mogul clients", "pro+": "all Pro / Empire / Mogul users", all_users: "ALL users" }[form.target_segment] || form.target_segment;
    if (!window.confirm(`Broadcast "${form.headline}" to ${segLabel}? This sends to real accounts and can't be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ad-sync", form);
      toast.success(`Broadcast to ${data.target_count} accounts.`);
      setForm({ headline: "", body: "", cta_url: "", target_segment: "all_mogul" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-adsync">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Megaphone size={20} className="text-cyan-300" /> Mogul Ad Sync</h2>
      <p className="text-slate-400 text-sm mt-1">Broadcast an ad across the Mogul network. Choose your segment, send.</p>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0A192F] border border-white/10 rounded-sm p-5 space-y-3">
          <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Headline" data-testid="admin-adsync-headline" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Body copy" rows={4} data-testid="admin-adsync-body" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          <input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="CTA URL (https://...)" data-testid="admin-adsync-url" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
          <select value={form.target_segment} onChange={(e) => setForm({ ...form, target_segment: e.target.value })} data-testid="admin-adsync-segment" className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm">
            <option value="all_mogul">All Mogul Clients</option>
            <option value="pro+">Pro / Empire / Mogul</option>
            <option value="all_users">All Users</option>
          </select>
          <button onClick={broadcast} disabled={busy} data-testid="admin-adsync-broadcast" className="w-full py-3 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-[#33F3FF] disabled:opacity-60">
            {busy ? "Broadcasting…" : "Broadcast"}
          </button>
        </div>

        <div className="bg-[#0A192F] border border-white/10 rounded-sm p-5 max-h-[60vh] overflow-y-auto">
          <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300 mb-3">Recent broadcasts</p>
          <div className="space-y-2">
            {syncs.map((s) => (
              <div key={s.sync_id} className="p-3 border border-white/5" data-testid={`admin-adsync-item-${s.sync_id}`}>
                <p className="text-white text-sm font-medium">{s.headline}</p>
                <p className="text-slate-400 text-xs mt-1">{s.target_segment} · {s.target_count} accounts · {s.created_at?.slice(0, 10)}</p>
              </div>
            ))}
            {syncs.length === 0 && <p className="text-slate-500 text-sm">No broadcasts yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCommandCenter;
